/**
 * Configured Axios instance for the Impact frontend.
 *
 * Auth model: OAuth 2.0 Bearer token (Drupal Simple OAuth module).
 *   - Authorization: Bearer {access_token} read from localStorage["token"] on
 *     every request. Token is stored and refreshed by AuthContext (PKCE flow).
 *   - No session cookies or CSRF tokens required.
 *
 * Error handling:
 *   - 401 → dispatch drupal:auth:logout event so AuthContext can clear state
 *   - 429 → retry after Retry-After header delay
 */
import axios from 'axios';

const BASE_URL = import.meta.env.VITE_DRUPAL_BASE_URL || '';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Request interceptor ────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    const stored = localStorage.getItem('token');
    if (stored) {
      try {
        const { access_token, token_type } = JSON.parse(stored);
        if (access_token) {
          config.headers['Authorization'] = `${token_type || 'Bearer'} ${access_token}`;
        }
      } catch { /* malformed entry — skip */ }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response interceptor ───────────────────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    // 401 — token expired or revoked: signal AuthContext to clear state
    if (response?.status === 401 && !config._retry) {
      window.dispatchEvent(new CustomEvent('drupal:auth:logout'));
      return Promise.reject(error);
    }

    // 429 — rate limited: wait for Retry-After then replay the request once
    if (response?.status === 429 && !config._retry) {
      config._retry = true;
      const retryAfter = parseInt(response.headers['retry-after'] || '5', 10);
      await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000));
      return apiClient(config);
    }

    return Promise.reject(error);
  },
);

export default apiClient;
